import clsx from 'clsx'
import page from '@/pages/NewDeploymentPage.module.css'
import styles from './Infrastructure.module.css'

export interface LookupOption {
  value: string
  label: string
  disabled?: boolean
}

interface LookupSelectProps {
  id: string
  label: string
  required?: boolean
  value: string
  options: LookupOption[]
  onChange: (value: string) => void
  /** The parent that has not been chosen yet, for example "an environment". While set, the select is disabled and says so. */
  waitingFor?: string
  isLoading?: boolean
  isError?: boolean
  onRetry?: () => void
  /** What to say when the lookup succeeded but returned nothing. */
  emptyMessage: string
  /** What to say when the lookup failed. */
  errorMessage?: string
  placeholder?: string
  hint?: string
  /** A validation message for the current value. */
  problem?: string | null
}

/**
 * A dropdown over fetched options. It owns the four states every lookup needs: waiting for its parent, loading, failed
 * (with Retry) and empty. Used by every select in the Infrastructure flow so they all behave the same.
 */
export function LookupSelect({
  id,
  label,
  required,
  value,
  options,
  onChange,
  waitingFor,
  isLoading,
  isError,
  onRetry,
  emptyMessage,
  errorMessage,
  placeholder = 'Select…',
  hint,
  problem,
}: LookupSelectProps) {
  const describedBy = `${id}-status`
  const empty = !waitingFor && !isLoading && !isError && options.length === 0
  const disabled = !!waitingFor || !!isLoading || !!isError || empty

  return (
    <div className={page.formGroup}>
      <label htmlFor={id} className={page.label}>
        {label} {required && <span className={page.required}>*</span>}
      </label>
      <select
        id={id}
        className={clsx(page.select, problem && page.inputError)}
        value={value}
        disabled={disabled}
        aria-describedby={describedBy}
        aria-invalid={problem ? true : undefined}
        aria-required={required || undefined}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
      <div id={describedBy} aria-live="polite">
        {waitingFor ? (
          <span className={page.fieldHint}>Choose {waitingFor} first.</span>
        ) : isLoading ? (
          <span className={styles.status}>Loading {label.toLowerCase()}…</span>
        ) : isError ? (
          <span role="alert" className={page.errorMsg}>
            {errorMessage ?? `Could not load ${label.toLowerCase()}.`}{' '}
            {onRetry && (
              <button type="button" className={styles.linkButton} onClick={onRetry}>
                Retry
              </button>
            )}
          </span>
        ) : empty ? (
          <span className={page.fieldHint}>{emptyMessage}</span>
        ) : hint ? (
          <span className={page.fieldHint}>{hint}</span>
        ) : null}
        {problem && (
          <span role="alert" className={page.errorMsg}>
            {problem}
          </span>
        )}
      </div>
    </div>
  )
}
